import { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { Loader2, ShieldCheck, XCircle } from "lucide-react";
import type { OAuthAuthorizationDetails } from "@supabase/supabase-js";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";

const PENDING_KEY = "pendingOAuthAuthorizationId";

const SCOPE_LABELS: Record<string, string> = {
  email: "See your email address",
};

function redirectHost(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

export default function OAuthConsentPage() {
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated, initialized } = useAuth();

  const [details, setDetails] = useState<OAuthAuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const loadedRef = useRef(false);
  // Resolved once: the stash is cleared after use, and this effect can re-run
  // (dep changes, Strict Mode), so later runs must not re-read storage.
  const authorizationIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!initialized) return;

    if (!authorizationIdRef.current) {
      authorizationIdRef.current =
        new URLSearchParams(location.search).get("authorization_id") ??
        sessionStorage.getItem(PENDING_KEY);
    }
    const authorizationId = authorizationIdRef.current;

    if (!authorizationId) {
      setError("Missing authorization request.");
      return;
    }

    if (!isAuthenticated) {
      sessionStorage.setItem(PENDING_KEY, authorizationId);
      history.replace("/login");
      return;
    }

    sessionStorage.removeItem(PENDING_KEY);
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      const { data, error: err } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (err || !data) {
        setError(err?.message ?? "This authorization request is invalid or has expired.");
        return;
      }
      if ("redirect_url" in data) {
        // Already consented: bounce straight back to the client.
        window.location.assign(data.redirect_url);
        return;
      }
      setDetails(data);
    })();
  }, [initialized, isAuthenticated, location.search, history]);

  const decide = async (approve: boolean) => {
    if (!details) return;
    setSubmitting(true);
    const { data, error: err } = approve
      ? await supabase.auth.oauth.approveAuthorization(details.authorization_id)
      : await supabase.auth.oauth.denyAuthorization(details.authorization_id);
    if (err || !data) {
      setError(err?.message ?? "Something went wrong. Please try again.");
      setSubmitting(false);
      return;
    }
    window.location.assign(data.redirect_url);
  };

  const scopes = details?.scope.split(" ").filter(Boolean) ?? [];

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
          <div className="max-w-sm w-full space-y-5">
            {error ? (
              <div className="text-center space-y-3">
                <XCircle className="w-10 h-10 text-destructive mx-auto" />
                <p className="text-foreground" role="alert">{error}</p>
              </div>
            ) : !details ? (
              <div className="text-center space-y-3">
                <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
                <p className="text-muted-foreground">Loading request...</p>
              </div>
            ) : (
              <>
                <div className="text-center space-y-2">
                  <ShieldCheck className="w-10 h-10 text-primary mx-auto" />
                  <h1 className="text-lg font-semibold text-foreground">
                    Authorize {details.client.name || "this app"}?
                  </h1>
                  {details.client.uri && (
                    <p className="text-xs text-muted-foreground break-all">{details.client.uri}</p>
                  )}
                </div>

                <div className="bg-surface border border-border rounded-lg p-4 space-y-3 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground">You will be sent back to</div>
                    <div className="font-mono font-medium text-foreground break-all" data-testid="redirect-host">
                      {redirectHost(details.redirect_uri)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">This app will be able to</div>
                    <ul className="list-disc pl-5 text-foreground">
                      <li>Read, create, and change your Brain Dump tasks, tags, and recurring tasks</li>
                      {scopes.map((s) => (
                        <li key={s}>{SCOPE_LABELS[s] ?? s}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Signed in as {details.user.email}. Only approve apps you trust; you can revoke access in Settings.
                  </div>
                </div>

                <div className="flex gap-3">
                  <button className="btn-secondary flex-1" disabled={submitting} onClick={() => decide(false)}>
                    Deny
                  </button>
                  <button className="btn-primary flex-1" disabled={submitting} onClick={() => decide(true)}>
                    Approve
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
